// 47224 (K-S10): the issue forms' view of the document field rules (docFieldRules.mjs). Reads GET /api/document-field-rules once per mount and, only when a
// required rule of this document is read from the customer record (NCC, RCCM, billing address, email, address), that customer's row.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { isRequired, missingRequired, missingText, needsPartner, cancelRequired, LOADING } from './docFieldRules.mjs';

// one read shared by every form on the page (the rules change only from the settings card, which calls forgetFieldRules() after a save)
let cached = null;
export const loadFieldRules = () => (cached = cached || Promise.resolve().then(() => api.getDocumentFieldRules()).then((r) => (r && r.fields) || []).catch((e) => { cached = null; throw e; }));
export const forgetFieldRules = () => { cached = null; };

export default function useDocFieldRules(kind, partnerId = null) {
  const { t, i18n } = useTranslation('invoicing');
  const [fields, setFields] = useState(null);
  const [partner, setPartner] = useState(undefined);   // undefined = not read yet, null = unreadable / not found
  useEffect(() => { let alive = true; loadFieldRules().then((f) => alive && setFields(f)).catch(() => alive && setFields([])); return () => { alive = false; }; }, []);
  const wantPartner = !!partnerId && needsPartner(fields, kind);
  useEffect(() => {
    if (!wantPartner) return undefined;
    let alive = true;
    Promise.resolve().then(() => api.getPartners()).then((rows) => alive && setPartner((Array.isArray(rows) ? rows : []).find((p) => String(p.id) === String(partnerId)) || null)).catch(() => alive && setPartner(null));
    return () => { alive = false; };
  }, [wantPartner, partnerId]);
  return {
    req: (field) => isRequired(fields, kind, field),
    cancelReq: () => cancelRequired(fields, kind),   // 47233: is a cancel reason required for this document (avoirs: cancel_avoir reads the same rule)
    mark: (field) => (isRequired(fields, kind, field) ? ' *' : ''),
    missing: (values) => (!kind ? [] : fields === null || (wantPartner && partner === undefined) ? [LOADING] : missingRequired(fields, kind, values, partner || null)),
    why: (missing) => (missing && missing.length ? missingText(missing, i18n.language, t) : undefined),   // the Issue button's disabled reason
  };
}

// one line under the form: what is still missing to issue (form fields and customer-record fields), in the engine's words
export function DocRequiredNote({ missing }) {
  const { t, i18n } = useTranslation('invoicing');
  if (!missing || !missing.length || missing[0].loading) return null;
  const onPartner = missing.some((f) => f.partner_column);
  return (
    <div className="callout warn" role="status" data-testid="doc-required-note" style={{ margin: '8px 0' }}>
      <span style={{ flex: 1, minWidth: 0 }}>{missingText(missing, i18n.language, t)}{onPartner ? ` ${t('dfrFillOnCustomer')}` : ''}</span>
    </div>
  );
}
